/**
 * Brand Products Modal
 *
 * Modal to preview all products for a brand at each store.
 * Shows per-store product counts and inventory details.
 *
 * @module HomeLayoutManager/components/BrandProductsModal
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Tooltip,
  SelectChangeEvent,
  ListSubheader,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  NewReleases as NewIcon,
  TrendingUp as PopularIcon,
  Warning as RestrictedIcon,
  Inventory as InventoryIcon,
  LocalOffer as BrandsIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import {
  fetchStores,
  fetchBrandProductCount,
  fetchBrandProducts as fetchBrandProductsCached,
} from '../api';
import type { Store } from '../types';
import { useStoreVisibility } from '../../../hooks/useStoreVisibility';

interface Product {
  id: string;
  name: string;
  price: number;
  priceTier1?: number;
  stock: number;
  sku?: string;
  barcode?: string;
  isNew?: boolean;
  isPopular?: boolean;
  isRestricted?: boolean;
  isSellable?: boolean;
  attachments?: Array<{ thumbnailUrl?: string; downloadUrl?: string; url?: string }>;
  brand?: string | { name: string };
}

interface BrandProductsModalProps {
  open: boolean;
  onClose: () => void;
  brandName: string;
  /** Optional initial store ID - when provided, modal opens showing this store */
  initialStoreId?: string;
}

/** Wrapper to cast shared api return type to local Product interface */
async function fetchBrandProducts(
  brandName: string,
  storeId: string | null,
  limit = 100
): Promise<Product[]> {
  return fetchBrandProductsCached(brandName, storeId, limit) as Promise<Product[]>;
}

function formatPrice(price: number | undefined): string {
  if (price === undefined || price === null) return '-';
  return `R${(price / 100).toFixed(2)}`;
}

export default function BrandProductsModal({
  open,
  onClose,
  brandName,
  initialStoreId,
}: BrandProductsModalProps): React.ReactElement {
  const { isStoreVisible } = useStoreVisibility();
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>(initialStoreId || '');
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoadingStores, setIsLoadingStores] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [storeProductCounts, setStoreProductCounts] = useState<Record<string, number>>({});
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);

  useEffect(() => {
    if (open && brandName) {
      setIsLoadingStores(true);
      setIsLoadingCounts(true);
      setSelectedStoreId(initialStoreId || '');
      setProducts([]);
      setSearchQuery('');

      fetchStores().then(async (storeData) => {
        const visible = storeData.filter(isStoreVisible);
        setStores(visible);
        setIsLoadingStores(false);

        // Fetch product counts in small batches to avoid 429 rate limiting
        const counts: Record<string, number> = {};
        const batchSize = 3;
        for (let i = 0; i < visible.length; i += batchSize) {
          const batch = visible.slice(i, i + batchSize);
          await Promise.all(
            batch.map(async (store) => {
              counts[store.id] = await fetchBrandProductCount(brandName, store.externalId || store.id);
            })
          );
          setStoreProductCounts({ ...counts });
          if (i + batchSize < visible.length) {
            await new Promise((r) => setTimeout(r, 200));
          }
        }
        setIsLoadingCounts(false);

        // If initialStoreId was provided, always use it (even if 0 products)
        // Otherwise auto-select first store with products
        if (initialStoreId) {
          setSelectedStoreId(initialStoreId);
        } else {
          const storeWithProducts = visible.find((s) => counts[s.id] > 0);
          setSelectedStoreId(storeWithProducts?.id || visible[0]?.id || '');
        }
      });
    }
  }, [open, brandName, initialStoreId]);

  const sortedStores = useMemo(() => {
    const withProducts = stores.filter((s) => storeProductCounts[s.id] > 0);
    const withoutProducts = stores.filter((s) => !storeProductCounts[s.id] || storeProductCounts[s.id] === 0);
    return { withProducts, withoutProducts };
  }, [stores, storeProductCounts]);

  useEffect(() => {
    if (open && selectedStoreId && brandName) {
      const selectedStore = stores.find((s) => s.id === selectedStoreId);
      const storeExternalId = selectedStore?.externalId || selectedStoreId;

      setIsLoadingProducts(true);
      fetchBrandProducts(brandName, storeExternalId).then((data) => {
        setProducts(data);
        setIsLoadingProducts(false);
      });
    }
  }, [open, selectedStoreId, brandName, stores]);

  const handleStoreChange = useCallback((event: SelectChangeEvent<string>) => {
    setSelectedStoreId(event.target.value);
  }, []);

  const filteredProducts = products.filter((product) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      product.name.toLowerCase().includes(query) ||
      product.sku?.toLowerCase().includes(query) ||
      product.barcode?.toLowerCase().includes(query)
    );
  });

  const selectedStore = stores.find((s) => s.id === selectedStoreId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: '#ab47bc', width: 36, height: 36 }}>
              <BrandsIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" component="span">
                {brandName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Products for this brand
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            p: 2,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>Store</InputLabel>
            <Select
              value={isLoadingStores ? '' : selectedStoreId}
              onChange={handleStoreChange}
              label="Store"
              disabled={isLoadingStores || isLoadingCounts}
            >
              {sortedStores.withProducts.length > 0 && (
                <ListSubheader sx={{ bgcolor: 'success.light', color: 'success.contrastText', lineHeight: '32px' }}>
                  Stores with products ({sortedStores.withProducts.length})
                </ListSubheader>
              )}
              {sortedStores.withProducts.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Typography variant="body2">{store.name}</Typography>
                    <Chip
                      label={`${storeProductCounts[store.id]} products`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </MenuItem>
              ))}
              {sortedStores.withProducts.length > 0 && sortedStores.withoutProducts.length > 0 && (
                <Divider />
              )}
              {sortedStores.withoutProducts.length > 0 && (
                <ListSubheader sx={{ bgcolor: 'grey.200', color: 'text.secondary', lineHeight: '32px' }}>
                  No products for this brand
                </ListSubheader>
              )}
              {sortedStores.withoutProducts.map((store) => (
                <MenuItem key={store.id} value={store.id} sx={{ opacity: 0.5, '&:hover': { opacity: 0.7 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{store.name}</Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>No products</Typography>
                  </Box>
                </MenuItem>
              ))}
              {isLoadingCounts && stores.length > 0 && (
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">Loading product counts...</Typography>
                  </Box>
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: 300 }}
          />

          <Box sx={{ ml: 'auto' }}>
            <Chip
              icon={<InventoryIcon />}
              label={`${filteredProducts.length} product${filteredProducts.length !== 1 ? 's' : ''}`}
              color={filteredProducts.length > 0 ? 'primary' : 'default'}
              variant="outlined"
            />
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          {(isLoadingStores || isLoadingProducts || isLoadingCounts) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {!isLoadingStores && !isLoadingProducts && !isLoadingCounts && filteredProducts.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <InventoryIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                {searchQuery
                  ? 'No products match your search'
                  : `No products found for "${brandName}" at ${selectedStore?.name || 'this store'}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Products may not be assigned to this brand or may be out of stock at this location
              </Typography>
            </Box>
          )}

          {!isLoadingStores && !isLoadingProducts && !isLoadingCounts && filteredProducts.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width={60}>Image</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Stock</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts.map((product) => {
                    const imageUrl =
                      product.attachments?.[0]?.thumbnailUrl ||
                      product.attachments?.[0]?.downloadUrl ||
                      product.attachments?.[0]?.url;

                    return (
                      <TableRow
                        key={product.id}
                        sx={{
                          opacity: product.stock > 0 ? 1 : 0.5,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <TableCell>
                          <Avatar
                            src={imageUrl}
                            variant="rounded"
                            sx={{ width: 40, height: 40, bgcolor: 'grey.200' }}
                          >
                            <CategoryIcon fontSize="small" />
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 250 }}>
                            {product.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {product.sku || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500}>
                            {formatPrice(product.priceTier1 || product.price)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={product.stock}
                            size="small"
                            color={product.stock > 0 ? 'success' : 'error'}
                            variant="outlined"
                            sx={{ minWidth: 50, height: 22 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {product.isNew && (
                              <Tooltip title="New">
                                <NewIcon sx={{ fontSize: 16, color: 'info.main' }} />
                              </Tooltip>
                            )}
                            {product.isPopular && (
                              <Tooltip title="Popular">
                                <PopularIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                              </Tooltip>
                            )}
                            {product.isRestricted && (
                              <Tooltip title="Restricted (age verification)">
                                <RestrictedIcon sx={{ fontSize: 16, color: 'error.main' }} />
                              </Tooltip>
                            )}
                            {!product.isSellable && (
                              <Tooltip title="Not sellable">
                                <Chip label="Disabled" size="small" sx={{ height: 18, fontSize: 10 }} />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
